import { FC } from 'react';
import { getPathWithRef } from 'helpers/utils';

interface Props {
  text: string;
  onLinkClick?: (href: string) => void;
}

type PlainTextSegment = {
  type: 'text';
  content: string;
};

type LinkTextSegment = {
  type: 'link';
  content: string;
  href: string;
};

export type TextSegment = PlainTextSegment | LinkTextSegment;

// Matches, in order: Markdown-style "[display text](url)" (captures the two
// pieces separately, since here content and href genuinely differ -- the
// url's own trailing ")" bounds the match, so a Wikipedia-style url containing
// its own "(...)" isn't supported), full http(s) URLs, root-relative paths
// like "/donate?p=mx" (guarded so it won't fire mid-word, e.g. "and/or"),
// email addresses like "help@oath.vote" (the "@" already disambiguates these,
// so no TLD allowlist is needed the way bare domains require one), and bare
// domains -- including subdomains, e.g. "app.oath.vote" -- restricted to a
// plausible TLD list to avoid false positives like "e.g." or "U.S."
const LINK_PATTERN =
  /(?:\[([^\]]+)\]\(([^\s)]+)\))|(?:https?:\/\/\S+)|(?:(?<!\w)\/\S+)|(?:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(?:\b(?:[a-zA-Z0-9-]+\.)+(?:com|org|net|io|gov|edu|co|vote|us)\b(?:\/\S*)?)/g;
// A matched link needs "https://" prepended when it's a bare domain
const LINK_NEEDING_HTTPS =
  /^(?:[a-zA-Z0-9-]+\.)+(?:com|org|net|io|gov|edu|co|vote|us)\b(?:\/\S*)?$/;
const needsHttps = (link: string) => LINK_NEEDING_HTTPS.test(link);

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const isEmail = (link: string) => EMAIL_PATTERN.test(link);

// LLM prose glues sentence punctuation straight onto a mentioned link (e.g.
// "see /donate?p=mx." or "at oath.vote, today"); \S+/\S* in LINK_PATTERN would
// otherwise swallow it into the href. A trailing ")" is only stripped when it
// has no matching "(" earlier in the link, so parenthesized paths survive.
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

const trimTrailingPunctuation = (raw: string): string => {
  const sentencePunctuation = raw.match(TRAILING_PUNCTUATION);
  const trimmed = sentencePunctuation ? raw.slice(0, -sentencePunctuation[0].length) : raw;
  return trimmed.endsWith(')') && !trimmed.includes('(') ? trimmed.slice(0, -1) : trimmed;
};

// Root-relative paths point back at Oath's own site, so they carry the same
// ?ref= attribution the structured donation_links CTA gets. "//host" is
// excluded -- it's protocol-relative to an external host, not our own path.
const buildHref = (link: string): string => {
  if (isEmail(link)) return `mailto:${link}`;
  if (needsHttps(link)) return `https://${link}`;
  if (link.startsWith('/') && !link.startsWith('//')) return getPathWithRef(link);
  return link;
};

export const parseTextWithLinks = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  LINK_PATTERN.lastIndex = 0;
  while ((match = LINK_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }

    const [displayText, markdownUrl] = [match[1], match[2]];
    if (displayText !== undefined && markdownUrl !== undefined) {
      // Markdown-style "[display text](url)": content and href diverge, and
      // the syntax's own closing ")" already bounds the match exactly, so
      // there's no trailing prose punctuation to trim off the outer match.
      const url = trimTrailingPunctuation(markdownUrl);
      segments.push({ type: 'link', content: displayText, href: buildHref(url) });
      lastIndex = match.index + match[0].length;
    } else {
      const link = trimTrailingPunctuation(match[0]);
      segments.push({ type: 'link', content: link, href: buildHref(link) });
      lastIndex = match.index + link.length;
    }
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return segments;
};

export const TextWithFormatting: FC<Props> = ({ text, onLinkClick }) => {
  const parsedText = parseTextWithLinks(text);
  return (
    <>
      {parsedText.map((item, index) =>
        item.type === 'link' ? (
          <a
            key={index}
            className="link-text"
            href={item.href}
            target="_blank"
            rel="noreferrer"
            onClick={() => onLinkClick?.(item.href)}
          >
            {item.content}
          </a>
        ) : (
          <span key={index}>{item.content}</span>
        )
      )}
    </>
  );
};
